import { BrowserRouter, Route, Routes } from "react-router-dom";
import { lazy, Suspense } from "react";
import Layout from "../components/Layout";

// ── Skeleton imports ─────────────────────────────────────────
import HomeSkeleton from "../components/skelton/HomeSkeleton";
import GallerySkeleton from "../components/skelton/GallerySkeleton";
import BlogSkeleton from "../components/skelton/BlogSkeleton";
import DonateSkeleton from "../components/skelton/DonateSkeleton";
import ContactSkeleton from "../components/skelton/ContactSkeleton";
import BookingSkeleton from "../components/skelton/BookingSkeleton";
import AboutSkeleton from "../components/skelton/AboutSkeleton";
import PageSkeleton from "../components/skelton/PageSkelton";
import ThankYou from "../components/thankyou/ThankYou";
import PaymentStatus from "../components/thankyou/PaymentStatus";
import TermsAndConditions from "../pages/cmsPages/TermsAndConditions";
import NotFound from "../components/NotFound";

// ── Lazy page imports ─────────────────────────────────────────
const Home = lazy(() => import("../pages/home/Home"));
const Contact = lazy(() => import("../pages/contact/Contact"));
const Gallery = lazy(() => import("../pages/gallery/Gallery"));
const QuickDonate = lazy(() => import("../pages/quickdonate/QuickDonate"));
const NityaSeva = lazy(
  () => import("../pages/registration/NityaSevaRegistration"),
);
const WhatIsISKCON = lazy(() => import("../pages/about/Whatisiskcon"));
const SrilaPrabhupada = lazy(() => import("../pages/about/Srilaprabhupada"));
const ISKCONLife = lazy(() => import("../pages/about/Iskconlife"));
const YDID = lazy(() => import("../pages/about/Ydid"));
const Kirtan = lazy(() => import("../pages/booking/Kirtan"));
const Katha = lazy(() => import("../pages/booking/Katha"));
const BookPuja = lazy(() => import("../pages/booking/Bookpuja"));
const BrahmanaBhojaSeva = lazy(
  () => import("../pages/donate/Brahmanabhojaseva"),
);
const PrivacyPolicy = lazy(() => import("../pages/cmsPages/Privacypolicy"));
const GyanDaanSeva = lazy(() => import("../pages/donate/Gyandaanseva"));
const SadhuSeva = lazy(() => import("../pages/donate/Sadhuseva"));
const SriVigraha = lazy(() => import("../pages/donate/Srivigraha"));
const RamNavami = lazy(() => import("../pages/donate/Ramnavami"));
const Janmashtami = lazy(() => import("../pages/donate/Janmashtami"));
const FoodForLife = lazy(() => import("../pages/donate/Foodforlife"));
const FestivalFoodForLife = lazy(() => import("../pages/donate/FestivalFoodForLifePage"));

// ── Nitya Seva Page Versions (Toggle by commenting / uncommenting) ───────────
const NityaSevak = lazy(() => import("../pages/donate/NityaSevaPageV2")); // V2: Immersive Devotional Master Experience (Active)
// const NityaSevak = lazy(() => import("../pages/donate/Nityasevak")); // V1: Classic Version
const NityaSevaBooking = lazy(
  () => import("../pages/donate/NityaSevaBookingPage"),
);

const RajbhogSeva = lazy(() => import("../pages/donate/Rajbhogseva"));
const DonateNow = lazy(() => import("../pages/donate/Donatenow"));
const DonateForm = lazy(() => import("../pages/donate/form/DonationForm"));
const SelfLessDonateForm = lazy(() => import("../forms/common/DonateForm"));
const LifetimeMembership = lazy(
  () => import("../pages/donate/Lifetimemembership"),
);
const Blog = lazy(() => import("../pages/blog/BlogList"));
const BlogDetail = lazy(() => import("../pages/blog/Detail"));

// ── Festival Pages ──────────────────────────────────────────
const AllFestivalsPage = lazy(
  () => import("../pages/festival/AllFestivalsPage"),
);
const FestivalDetailPage = lazy(
  () => import("../pages/festival/FestivalDetailPage"),
);
const FestivalBookingPage = lazy(
  () => import("../pages/festival/FestivalBookingPage"),
);

const EventsPage = lazy(() => import("../pages/events/ListPage"));
const EventDetail = lazy(() => import("../pages/events/DetailPage"));
const ForeignContribution = lazy(() => import("../forms/foreign/ForeignForm"));
const NityaSevaDashboard = lazy(
  () => import("../pages/dashboard/Memberdashboard"),
);
// ── Helper: wrap a page with its specific skeleton ───────────
const withSkeleton = (Page, Skeleton) => (
  <Suspense fallback={<Skeleton />}>
    <Page />
  </Suspense>
);

const Router = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Layout />}>
          {/* Home */}
          <Route index element={withSkeleton(Home, HomeSkeleton)} />

          {/* Events */}
          <Route
            path="/events"
            element={withSkeleton(EventsPage, PageSkeleton)}
          />
          <Route
            path="/events/public/:slug"
            element={withSkeleton(EventDetail, PageSkeleton)}
          />

          {/* About */}
          <Route
            path="/about/what-is-iskcon"
            element={withSkeleton(WhatIsISKCON, AboutSkeleton)}
          />
          <Route
            path="/about/srila-prabhupada"
            element={withSkeleton(SrilaPrabhupada, AboutSkeleton)}
          />
          <Route
            path="/about/iskcon-delhi"
            element={withSkeleton(ISKCONLife, AboutSkeleton)}
          />
          <Route
            path="/about/what-is-ydid"
            element={withSkeleton(YDID, AboutSkeleton)}
          />

          {/* Booking */}
          <Route
            path="/booking/kirtan"
            element={withSkeleton(Kirtan, BookingSkeleton)}
          />
          {/* <Route
            path="/booking/katha"
            element={withSkeleton(Katha, BookingSkeleton)}
          /> */}
          <Route
            path="/booking/offer-puja"
            element={withSkeleton(BookPuja, BookingSkeleton)}
          />

          {/* Donate / Seva */}
          <Route path="/donation/:slug" element={<DonateForm />} />
          <Route
            path="/donate/iskcon-ram-navami-2026"
            element={withSkeleton(RamNavami, DonateSkeleton)}
          />
          <Route
            path="/sri-krishna-janmashtami-2026"
            element={withSkeleton(Janmashtami, DonateSkeleton)}
          />
          <Route
            path="/donate/food-for-life"
            element={withSkeleton(FoodForLife, DonateSkeleton)}
          />
          {/* <Route
            path="/donate/food-for-life/:festivalSlug"
            element={withSkeleton(FestivalFoodForLife, DonateSkeleton)}
          /> */}
          <Route
            path="/donate/nitya-sevak-registration"
            element={withSkeleton(NityaSevak, DonateSkeleton)}
          />
          <Route
            path="/donate/nitya-seva-booking"
            element={withSkeleton(NityaSevaBooking, DonateSkeleton)}
          />
          <Route
            path="/booking/nitya-seva"
            element={withSkeleton(NityaSevaBooking, DonateSkeleton)}
          />
          <Route
            path="/nitya-seva/dashboard"
            element={withSkeleton(NityaSevaDashboard, DonateSkeleton)}
          />
          <Route
            path="/donate/brahmana-bhoja-seva"
            element={withSkeleton(BrahmanaBhojaSeva, DonateSkeleton)}
          />
          <Route
            path="/donate/sri-vigraha-seva"
            element={withSkeleton(SriVigraha, DonateSkeleton)}
          />
          <Route
            path="/donate/sadhu-seva"
            element={withSkeleton(SadhuSeva, DonateSkeleton)}
          />
          <Route
            path="/donate/gyan-daan-seva"
            element={withSkeleton(GyanDaanSeva, DonateSkeleton)}
          />
          <Route
            path="/donate/rajbhoga-seva"
            element={withSkeleton(RajbhogSeva, DonateSkeleton)}
          />
          <Route
            path="/donate/other-seva"
            element={withSkeleton(DonateNow, DonateSkeleton)}
          />
          <Route
            path="/foreign-contribution"
            element={withSkeleton(ForeignContribution, DonateSkeleton)}
          />
          <Route
            path="/lifetime-membership"
            element={withSkeleton(LifetimeMembership, DonateSkeleton)}
          />

          {/* SELFLESS DONATION */}
          <Route
            path="/donate/selfless/:slug"
            element={<SelfLessDonateForm />}
          />

          {/* CMS PAGES */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route
            path="/terms-and-conditions"
            element={<TermsAndConditions />}
          />

          {/* Gallery, Blog, Contact */}
          <Route
            path="gallery"
            element={withSkeleton(Gallery, GallerySkeleton)}
          />
          <Route path="blog" element={withSkeleton(Blog, BlogSkeleton)} />
          <Route
            path="blog/:id"
            element={withSkeleton(BlogDetail, BlogSkeleton)}
          />

          {/* Festivals */}
          <Route
            path="festivals"
            element={withSkeleton(AllFestivalsPage, PageSkeleton)}
          />
          <Route
            path="festivals/:id"
            element={withSkeleton(FestivalDetailPage, PageSkeleton)}
          />
          <Route
            path="festivals/:id/booking"
            element={withSkeleton(FestivalBookingPage, DonateSkeleton)}
          />
          <Route
            path="festival/:id"
            element={withSkeleton(FestivalDetailPage, PageSkeleton)}
          />
          <Route
            path="/donate/festival-booking"
            element={withSkeleton(FestivalBookingPage, DonateSkeleton)}
          />
          <Route
            path="/donate/festival-seva-booking"
            element={withSkeleton(FestivalBookingPage, DonateSkeleton)}
          />
          <Route
            path="/festival/booking"
            element={withSkeleton(FestivalBookingPage, DonateSkeleton)}
          />
          <Route
            path="contact"
            element={withSkeleton(Contact, ContactSkeleton)}
          />

          {/* Misc */}
          <Route
            path="quick-donation"
            element={withSkeleton(QuickDonate, DonateSkeleton)}
          />
          <Route
            path="nitya-sevak-registration"
            element={withSkeleton(NityaSeva, DonateSkeleton)}
          />

          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/payment-status" element={<PaymentStatus />} />

          {/* Fallback for every URL that does not match a route above */}
          <Route path="*" element={<NotFound />} />
        </Route>
        {/* Standalone Landing Page (Bina Header & Footer ke) */}
        <Route
          path="/donate/food-for-life/:festivalSlug"
          element={withSkeleton(FestivalFoodForLife, DonateSkeleton)}
        />
      </Routes>
    </BrowserRouter>
  );
};

export default Router;
